import {Component, computed, inject, signal, effect} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {ToastModule} from 'primeng/toast';
import {TooltipModule} from 'primeng/tooltip';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {MultiTabStepBaseComponent} from '../../../../../../shared/tabs/multi-tab-step-base.component';
import {TabStatus} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {
  RFQ_MAIL_PREVIEW_FIELD_MAP,
  RFQ_MAIL_PREVIEW_SECTIONS,
  Step821RfqMailPreviewComponent,
} from './step-8-2-1-rfq-mail-preview/step-8-2-1-rfq-mail-preview.component';
import {
  ProjectChapterType,
  ProjectStepType,
  TenderInvitationPreviewControllerService,
  TenderInvitationPreviewResponse,
  TenderPhaseType,
  InvitationOfferOverviewResponse,
  RfqControllerService,
  ContractParametersContainerResponse,
} from '@frontend/shared';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';
import {Step823RfqPreviewPriceListComponent} from './step-8-2-3-rfq-price-list/step-8-2-3-rfq-preview-price-list.component';
import {Step824ProposalDocumentGuidanceComponent} from './step-8-2-4-rfq-proposal-document-guidance/step-8-2-4-proposal-document-guidance.component';
import {firstValueFrom, of, catchError} from 'rxjs';
import {
  RFQ_PROPOSAL_GUIDANCE_SECTIONS,
  RFQ_PROPOSAL_GUIDANCE_FIELD_MAP,
} from './step-8-2-4-rfq-proposal-document-guidance/proposal-guidance-preview/proposal-guidance-preview.component';
import {Step822RfqSystemIntegrationComponent} from './step-8-2-2-rfq-system-integration/step-8-2-2-rfq-system-integration.component';
import {rxResource} from '@angular/core/rxjs-interop';
import {ContractParametersService} from '../step-8-1-proposal-parameter/contract-parameters.service';
import {ProposalParameterPanelsComponent} from '../step-8-1-proposal-parameter/components/contract-parameter-panels/proposal-parameter-panels.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {CardTitleComponent} from '../../../../../../shared/components/header/card-title/card-title.component';
import {TranslocoPipe} from '@jsverse/transloco';
import {calculateRichTextSectionStatus} from '../../../../../../shared/services/project-step-rich-text.service';
import {createTabStatusSignal} from '../../../../../../shared/tabs/tab-utils';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

const INITIAL_CONTRACT_CONTAINER: ContractParametersContainerResponse = {
  contractParameters: {
    contractTotalTerms: 0,
    terminationPeriod: 0,
    solutionType: '',
    implementationMethod: '',
    licenseModel: '',
    paymentTerm: '',
    languages: [],
  },
  implementationTimelines: [],
};

@Component({
  imports: [
    FormsModule,
    ButtonModule,
    CardModule,
    ToastModule,
    TooltipModule,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Step821RfqMailPreviewComponent,
    Step823RfqPreviewPriceListComponent,
    Step824ProposalDocumentGuidanceComponent,
    PreviousStepIncompleteNoticeComponent,
    Step822RfqSystemIntegrationComponent,
    ProposalParameterPanelsComponent,
    LoadingComponent,
    CardTitleComponent,
    TranslocoPipe,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-8-2-invitation-preview.component.html',
})
export class Step82InvitationPreviewComponent extends MultiTabStepBaseComponent {
  private readonly tenderInvitationPreviewService = inject(
    TenderInvitationPreviewControllerService,
  );
  private readonly contractParametersService = inject(ContractParametersService);
  private readonly rfqController = inject(RfqControllerService);

  protected readonly mailPreviewTabStatus = createTabStatusSignal();
  protected readonly proposalDocumentGuidanceTabStatus = createTabStatusSignal();

  protected readonly readOnlyTabStatus: TabStatus = {totalSections: 1, completedSections: 1};

  readonly invitationPreviewResponse = signal<TenderInvitationPreviewResponse | undefined>(
    undefined,
  );

  readonly invitationOfferOverview = signal<InvitationOfferOverviewResponse>({
    locale: '',
    currencyCode: '',
    yearsInScope: 0,
    companyInfosResponse: [],
    serviceFeesResponse: [],
    implementationActivityFeesResponse: [],
    integrationSetupFeesResponse: [],
    integrationOtherFeesResponse: [],
  });

  readonly contractParametersResource = rxResource({
    params: () => ({
      projectId: this.projectId,
      isStepPrerequisiteCompleted: this.isStepPrerequisiteCompleted(),
    }),
    stream: ({params: request}) => {
      if (request.projectId === undefined || !request.isStepPrerequisiteCompleted) {
        return of(INITIAL_CONTRACT_CONTAINER);
      }

      return this.contractParametersService.loadContractParametersContainer(request.projectId).pipe(
        catchError((err) => {
          console.error('Initial load of contract parameters failed:', err);
          return of(INITIAL_CONTRACT_CONTAINER);
        }),
      );
    },
  });

  constructor() {
    super();

    effect(() => {
      const projectId = this.projectId;
      const isStepPrerequisiteCompleted = this.isStepPrerequisiteCompleted();

      if (!projectId || !isStepPrerequisiteCompleted) return;

      this.isLoadingState.set(true);

      Promise.all([
        firstValueFrom(
          this.tenderInvitationPreviewService.getTenderInvitationPreview({
            projectId: projectId,
            phase: TenderPhaseType.RFQ,
          }),
        ),
        firstValueFrom(
          this.rfqController.getInvitationOfferOverviewDraft({
            projectId: this.projectId,
          }),
        ),
      ])
        .then(([invitationPreview, invitationOfferOverview]) => {
          this.invitationPreviewResponse.set(invitationPreview);
          this.invitationOfferOverview.set(invitationOfferOverview);
          this.calculateInitialTabStatuses();
        })
        .finally(() => {
          this.isLoadingState.set(false);
        });
    });
  }

  private calculateInitialTabStatuses(): void {
    const invitationData = this.invitationPreviewResponse();
    if (!invitationData) return;

    this.calculateMailPreviewTabStatuses(invitationData);
    this.calculateProposalDocumentGuidanceTabStatuses(invitationData);
  }

  private calculateMailPreviewTabStatuses(invitationData: TenderInvitationPreviewResponse): void {
    const mailCompleted = calculateRichTextSectionStatus(
      invitationData,
      RFQ_MAIL_PREVIEW_SECTIONS,
      RFQ_MAIL_PREVIEW_FIELD_MAP,
    );

    const mailStatus: TabStatus = {
      totalSections: RFQ_MAIL_PREVIEW_SECTIONS.length,
      completedSections: mailCompleted,
    };

    this.onMailPreviewStatusChange(mailStatus);
  }

  private calculateProposalDocumentGuidanceTabStatuses(
    invitationData: TenderInvitationPreviewResponse,
  ): void {
    const guidanceCompleted = calculateRichTextSectionStatus(
      invitationData,
      RFQ_PROPOSAL_GUIDANCE_SECTIONS,
      RFQ_PROPOSAL_GUIDANCE_FIELD_MAP,
    );

    const guidanceStatus: TabStatus = {
      totalSections: RFQ_PROPOSAL_GUIDANCE_SECTIONS.length,
      completedSections: guidanceCompleted,
    };

    this.onProposalDocumentGuidanceStatusChange(guidanceStatus);
  }

  onMailPreviewStatusChange(status: TabStatus): void {
    this.mailPreviewTabStatus.update(status);
  }

  onProposalDocumentGuidanceStatusChange(status: TabStatus): void {
    this.proposalDocumentGuidanceTabStatus.update(status);
  }

  protected readonly isStepPrerequisiteCompleted = computed(() => {
    return this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFQ, stepId: ProjectStepType.RFQ_PROPOSAL_PARAMETER},
    ]);
  });
}
